import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  map,
  Observable,
  of,
  startWith,
} from 'rxjs';

import { BaseKPI } from '../types';

type PayloadType<T extends PayloadAction> = ConstructorParameters<T>[0];
type PayloadAction = { new (...args: any): { type: string; payload: any } };
type TriggerAction = { new (...args: any): { type: string } };
type OperationChart<T> = {
  loading: boolean;
  error: any | undefined;
  data: T | undefined;
};

@Injectable()
export abstract class ChartService<T extends BaseKPI<any, any>> {
  private readonly actions$ = inject(Actions);

  private readonly store$ = inject(Store);

  public abstract readonly chart$: Observable<{
    loading: boolean;
    ngClass: string[];
    data: T;
  }>;

  protected chartsDataLoader<
    RequestAction extends TriggerAction,
    SuccessAction extends PayloadAction,
    FailedAction extends PayloadAction,
  >(actions: {
    request: RequestAction;
    success: SuccessAction;
    failure: FailedAction;
  }): Observable<OperationChart<PayloadType<SuccessAction>>> {
    const observable$ = combineLatest([
      this.actions$.pipe(
        ofType(new actions.success().type),
        map(({ payload }) => payload),
        startWith(undefined),
      ),
      this.actions$.pipe(
        ofType(new actions.failure().type),
        startWith(undefined),
      ),
      this.actions$.pipe(
        ofType(new actions.failure().type),
        startWith(undefined),
      ),
    ]).pipe(
      map(([success, failure]) => {
        const response: OperationChart<PayloadType<SuccessAction>> = {
          loading: true,
          error: failure,
          data: success,
        };

        if (success !== undefined || failure !== undefined) {
          response.loading = false;
        }

        return response;
      }),
      catchError((failure) => {
        const response: OperationChart<T> = {
          loading: true,
          error: failure,
          data: undefined,
        };

        return of(response);
      }),
    );

    this.triggerDispatch(actions.request);
    return observable$;
  }

  private static readonly autoDispatched = new Map<
    TriggerAction,
    ReturnType<typeof setTimeout>
  >();

  private triggerDispatch(action: TriggerAction): void {
    clearTimeout(ChartService.autoDispatched.get(action));
    ChartService.autoDispatched.set(
      action,
      setTimeout(() => {
        this.store$.dispatch(new action());
      }, 100),
    );
  }
}
